import { useState } from 'react';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, type Theme,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { useCanManageAccess } from '@/access/useCanManageAccess';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { payApiClient } from '@/api/client';
import type { Gateway } from '@/api/models';
import { useApiResource } from '@/hooks/useApiResource';
import { ApiRequestError } from '@/api/errors';
import { GatewayAccountSecretsWriteDialog } from './GatewayAccountSecretsWriteDialog';
import { useGatewayDescriptor } from './useGatewayDescriptors';

interface GatewayAccountSecretsPanelProps {
  gatewayAccountId: string;
  /** The account's own gateway — carried through so the write dialog can ask for that gateway's actual credential fields. */
  gateway: Gateway;
}

/**
 * `GET /v1/admin/gateway-accounts/{id}/secrets` — metadata only, for every
 * role, with no exception (D-M). The only things ever rendered per field are
 * its name, whether it is set, the last 4 characters (present only when the
 * API itself returns them — never derived or guessed client-side), the
 * `sha256` fingerprint prefix, and the last rotation date. There is no
 * reveal button, no view action, no export, no debug path, for any role —
 * these do not exist anywhere in this component, not even hidden behind
 * `useCanManageAccess`.
 */
export function GatewayAccountSecretsPanel({ gatewayAccountId, gateway }: GatewayAccountSecretsPanelProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const canWrite = useCanManageAccess();
  const [writeOpen, setWriteOpen] = useState(false);
  // A field the gateway does not declare is one no adapter reads — written by
  // mistake, and the only kind the platform lets anyone remove. Until the
  // descriptor is known, nothing is offered as removable: guessing wrong here
  // would offer to destroy a credential the account depends on.
  const { descriptor } = useGatewayDescriptor(gateway);
  const [pendingRemoval, setPendingRemoval] = useState<string>();
  const [removing, setRemoving] = useState(false);
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.getAdminGatewayAccountSecrets(gatewayAccountId),
    gatewayAccountId,
  );

  function isUsedByGateway(fieldName: string): boolean {
    return descriptor === undefined || descriptor.credentialFields.some((field) => field.fieldName === fieldName);
  }

  async function handleRemove(fieldName: string) {
    setRemoving(true);
    try {
      await payApiClient.deleteAdminGatewayAccountSecretField(gatewayAccountId, fieldName);
      notify(translate('pay.gatewayAccounts.secrets.removeSuccess'), { type: 'success' });
      setPendingRemoval(undefined);
      reload();
    } catch (caught) {
      const message = caught instanceof ApiRequestError ? caught.message : undefined;
      notify(message ?? translate('pay.errorState.body'), { type: 'error' });
    } finally {
      setRemoving(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <LoadingState variant="list" rows={3} />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'access_denied') {
      return (
        <Card>
          <AccessDenied />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.gatewayAccounts.secrets.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  const fields = data?.fields ?? [];

  const headCellSx = (theme: Theme) => ({
    ...theme.custom.table.headCell,
    backgroundColor: theme.custom.table.headerBg,
    borderBottom: `1px solid ${theme.custom.borderStrong}`,
  });

  return (
    <Stack spacing={1.5}>
      {fields.length === 0 ? (
        <Card>
          <EmptyState
            title={translate('pay.gatewayAccounts.secrets.emptyTitle')}
            body={translate('pay.gatewayAccounts.secrets.emptyBody')}
            action={
              canWrite ? (
                <Button variant="contained" size="small" onClick={() => setWriteOpen(true)}>
                  {translate('pay.gatewayAccounts.secrets.writeAction')}
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Card sx={{ overflow: 'hidden' }}>
          {/* Wide table content scrolls inside the card (same containment rule as CustomDataGrid). */}
          <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>{translate('pay.gatewayAccounts.secrets.columnField')}</TableCell>
                  <TableCell sx={headCellSx}>{translate('pay.gatewayAccounts.secrets.columnIsSet')}</TableCell>
                  <TableCell sx={headCellSx}>{translate('pay.gatewayAccounts.secrets.columnLast4')}</TableCell>
                  <TableCell sx={headCellSx}>{translate('pay.gatewayAccounts.secrets.columnFingerprint')}</TableCell>
                  <TableCell sx={headCellSx}>{translate('pay.gatewayAccounts.secrets.columnRotatedAt')}</TableCell>
                  <TableCell sx={headCellSx} align="right">{translate('pay.gatewayAccounts.secrets.columnActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field) => (
                  <TableRow key={field.fieldName}>
                    <TableCell sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono, fontSize: '0.75rem' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{field.fieldName}</span>
                        {!isUsedByGateway(field.fieldName) && (
                          <Tooltip title={translate('pay.gatewayAccounts.secrets.unusedTooltip')}>
                            <Chip size="small" variant="outlined" label={translate('pay.gatewayAccounts.secrets.unusedChip')} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={translate(field.isSet ? 'pay.gatewayAccounts.secrets.isSetYes' : 'pay.gatewayAccounts.secrets.isSetNo')}
                        color={field.isSet ? 'success' : 'default'}
                        variant={field.isSet ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono, fontSize: '0.75rem' }}>
                      {field.last4 ?? translate('pay.gatewayAccounts.secrets.notAvailable')}
                    </TableCell>
                    <TableCell sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono, fontSize: '0.75rem' }}>
                      {field.fingerprint}
                    </TableCell>
                    <TableCell>{new Date(field.rotatedAt).toLocaleString('es-ES')}</TableCell>
                    <TableCell align="right">
                      {canWrite && !isUsedByGateway(field.fieldName) && (
                        <IconButton
                          size="small"
                          aria-label={translate('pay.gatewayAccounts.secrets.removeAction')}
                          onClick={() => setPendingRemoval(field.fieldName)}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
      {canWrite && fields.length > 0 && (
        <Stack direction="row">
          <Button size="small" variant="outlined" onClick={() => setWriteOpen(true)}>
            {translate('pay.gatewayAccounts.secrets.writeAction')}
          </Button>
        </Stack>
      )}
      <Dialog open={pendingRemoval !== undefined} onClose={() => setPendingRemoval(undefined)} maxWidth="xs" fullWidth>
        <DialogTitle>{translate('pay.gatewayAccounts.secrets.removeTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate('pay.gatewayAccounts.secrets.removeBody', { fieldName: pendingRemoval ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoval(undefined)}>{translate('pay.gatewayAccounts.secrets.removeCancel')}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={removing}
            onClick={() => pendingRemoval && handleRemove(pendingRemoval)}
          >
            {translate('pay.gatewayAccounts.secrets.removeConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <GatewayAccountSecretsWriteDialog
        open={writeOpen}
        gatewayAccountId={gatewayAccountId}
        gateway={gateway}
        onClose={() => setWriteOpen(false)}
        onSaved={() => {
          setWriteOpen(false);
          reload();
        }}
      />
    </Stack>
  );
}
